<template>
  <div v-if="show" class="education-page">
    <div class="education-info-list">
      <div v-for="(education, index) in data.educationInfo" class="education-info" :key="index">
        <EducationEditor
          ref="education"
          :education="education"
          :index="index"
          :setting="setting"
          :schoolOptions="schoolOptions"
          :majorOptions="majorOptions"
          :academicRatingOptions="academicRatingOptions"
          :educationLevelOptions="educationLevelOptions"
        />
        <van-button class="delete-btn" @click="deleteEducation(index)">
          <van-icon class="delete-icon iconfont iconRemove"></van-icon>
          <span>{{ $t(`${i18nPrefix}.editor.education.title.deleteEducation`) }}</span>
        </van-button>
        <van-cell class="delete-border"></van-cell>
      </div>
      <van-button class="add-btn" @click="addEducation">
        <van-icon class-prefix="add-icon iconfont iconAdd1"></van-icon>
        <span>{{ $t(`${i18nPrefix}.editor.education.title.addEducation`) }}</span>
      </van-button>
      <van-cell class="add-border"></van-cell>
    </div>

    <div class="language-info-list">
      <div v-for="(language, index) in data.languageInfo" class="language-info" :key="index">
        <LanguageEditor
          ref="language"
          :language="language"
          :setting="setting"
          :languageOptions="languageOptions"
          :levelOptions="levelOptions"
          :certificationOptions="certificationOptions"
        />
        <van-button class="delete-btn" @click="deleteLanguage(index)">
          <van-icon class="delete-icon iconfont iconRemove"></van-icon>
          <span>{{ $t(`${i18nPrefix}.editor.language.title.deleteLanguages`) }}</span>
        </van-button>
        <van-cell class="delete-border"></van-cell>
      </div>
      <van-button class="add-btn" @click="addLanguage">
        <van-icon class-prefix="add-icon iconfont iconAdd1"></van-icon>
        <span>{{ $t(`${i18nPrefix}.editor.language.title.addLanguages`) }}</span>
      </van-button>
      <van-cell class="add-border"></van-cell>
    </div>

    <van-dialog
      v-model="dialog.show"
      class="delete-dialog"
      show-cancel-button
      :title="dialog.title"
      :confirm-button-text="$t('common.btn.confirm')"
      :cancel-button-text="$t('common.btn.cancel')"
      @confirm="onConfirm"
      @cancel="onCancel"
    >
      {{ dialog.content || $t('common.message.confirmDelete') }}
    </van-dialog>
  </div>
</template>

<script>
import _ from 'lodash'
import { mapActions } from 'vuex'
import EducationEditor from '@/components/candidate/mobile/education.vue'
import LanguageEditor from '@/components/candidate/mobile/language.vue'
import { addI18nProperty } from '@/i18n'
import { STEP_TYPE } from '@/constant/candidate'
import mixin from './mixin'

export default {
  mixins: [mixin],
  components: {
    EducationEditor,
    LanguageEditor
  },
  props: {
    data: {
      type: Object,
      required: true
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.candidate',
      educationLevelOptions: [],
      schoolOptions: [],
      academicRatingOptions: [],
      majorOptions: [],
      levelOptions: [],
      languageOptions: [],
      certificationOptions: [],
      dialog: {
        show: false,
        content: null,
        title: null,
        confirm: null,
        cancel: null
      }
    }
  },
  computed: {
    setting () {
      return this.resumeEditorSetting?.[STEP_TYPE.EDUCATION]
    },
    educationForm () {
      if (this.show) {
        let educationForm = this.$refs.education
        if (!educationForm) {
          this.$nextTick(() => {
            educationForm = this.$refs.education
          })
        }
        return educationForm
      }
      return null
    },
    languageForm () {
      if (this.show) {
        let languageForm = this.$refs.language
        if (!languageForm) {
          this.$nextTick(() => {
            languageForm = this.$refs.language
          })
        }
        return languageForm
      }
      return null
    }
  },
  created () {
    this.initData()
  },
  mounted () {},
  methods: {
    ...mapActions('application', [
      'getEducationLevelList',
      'getAcademicRatingList',
      'getMajorList',
      'getSchoolList',
      'getLanguageList',
      'getLanguageLevelList',
      'getLanguageCertificationList'
    ]),
    async initData () {
      this.$emit('load', true)
      try {
        await Promise.all([
          this.loadEducationLevelList(),
          this.loadSchoolList(),
          this.loadAcademicRatingList(),
          this.loadMajorList(),
          this.loadLanguageList(),
          this.loadLanguageLevelList(),
          this.loadCertificationList()
        ])
      } finally {
        this.$emit('load', false)
      }
    },
    async submit (isPrevious) {
      const education = this.educationForm
      const language = this.languageForm
      let validate = true
      if (language) {
        const languageLength = language.length
        await Promise.all(_.times(languageLength, async (index) => {
          const result = await language[languageLength - 1 - index].submit()
          if (!result) {
            if (isPrevious) {
              this.data.languageInfo.splice(languageLength - 1 - index, 1)
            } else {
              validate = false
            }
          }
        }))
      }
      if (education) {
        const educationLength = education.length
        await Promise.all(_.times(educationLength, async (index) => {
          const result = await education[educationLength - 1 - index].submit()
          if (!result) {
            if (isPrevious) {
              this.data.educationInfo.splice(educationLength - 1 - index, 1)
            } else {
              validate = false
            }
          }
        }))
      }
      if (!isPrevious && validate) {
        if (this.data.educationInfo.length < this.setting.eduRequiredCount) {
          this.$toast({ message: this.$t(`${this.i18nPrefix}.message.educationCountRequired`) })
          validate = false
        }
      }
      return validate
    },
    deleteEducation (index) {
      this.dialog.show = true
      this.dialog.content = ''
      this.dialog.confirm = () => {
        this.data.educationInfo.splice(index, 1)
      }
    },
    addEducation () {
      this.data.educationInfo.push({
        schoolName: null,
        majorName: null,
        academicRating: null,
        educationLevel: null,
        startDate: null,
        endDate: null
      })
    },
    deleteLanguage (index) {
      this.dialog.show = true
      this.dialog.content = ''
      this.dialog.confirm = () => {
        this.data.languageInfo.splice(index, 1)
      }
    },
    addLanguage () {
      this.data.languageInfo.push({
        language: null,
        level: null,
        certification: null,
        score: null
      })
    },
    onConfirm () {
      this.dialog.confirm && this.dialog.confirm()
      this.dialog.show = false
      this.dialog.confirm = null
      this.dialog.cancel = null
    },
    onCancel () {
      this.dialog.cancel && this.dialog.cancel()
      this.dialog.show = false
      this.dialog.confirm = null
      this.dialog.cancel = null
    },
    async loadEducationLevelList () {
      const resp = await this.getEducationLevelList()
      if (resp) {
        this.educationLevelOptions = _.chain(resp.education_levels)
          .filter(item => (item.cn_name && item.en_name))
          .map((info) => {
            addI18nProperty(info, 'label', 'cn_name', 'en_name')
            return info
          })
          .value()
      }
    },
    async loadSchoolList () {
      const resp = await this.getSchoolList()
      if (resp) {
        this.schoolOptions = this.formatSimpleJobInfo(resp.schools)
      }
    },
    async loadAcademicRatingList () {
      const resp = await this.getAcademicRatingList()
      if (resp) {
        this.academicRatingOptions = _.chain(resp.academic_ratings)
          .filter(item => (item.cn_name && item.en_name))
          .map((info) => {
            addI18nProperty(info, 'label', 'cn_name', 'en_name')
            return info
          })
          .value()
      }
    },
    async loadMajorList () {
      const resp = await this.getMajorList()
      if (resp) {
        this.majorOptions = this.formatSimpleJobInfo(resp.majors)
      }
    },
    async loadLanguageList () {
      const resp = await this.getLanguageList()
      if (resp) {
        this.languageOptions = _.chain(resp.languages)
          .filter(item => (item.cn_name && item.en_name))
          .map((info) => {
            addI18nProperty(info, 'label', 'cn_name', 'en_name')
            return info
          })
          .value()
      }
      return this.languageOptions
    },
    async loadLanguageLevelList () {
      const resp = await this.getLanguageLevelList()
      if (resp) {
        this.levelOptions = _.chain(resp.language_levels)
          .filter(item => (item.cn_name && item.en_name))
          .map((info) => {
            addI18nProperty(info, 'label', 'cn_name', 'en_name')
            return info
          })
          .value()
      }
      return this.levelOptions
    },
    async loadCertificationList () {
      const resp = await this.getLanguageCertificationList()
      if (resp) {
        this.certificationOptions = _.chain(resp.certifications)
          .filter(item => (item.cn_name && item.en_name))
          .map((info) => {
            addI18nProperty(info, 'label', 'cn_name', 'en_name')
            return info
          })
          .value()
      }
      return this.certificationOptions
    },
    formatSimpleJobInfo (info) {
      return _.chain(info)
        .filter(item => (item.cn_name && item.name))
        .map(item => {
          const info = {
            ...item,
            value: item.id || item.value
          }
          addI18nProperty(info, 'label', 'cn_name', 'name')
          return info
        })
        .value()
    }
  }
}
</script>

<style lang="scss" scoped>
.education-page {
  padding: 20px 18px 0;

  .delete-btn {
    margin: 12px 0 12px;
    display: block;
    height: 36px;
    width: 100%;
    background: #fff;
    color: #909399;
    border: none;

    .delete-icon {
      display: inline-block;
      line-height: 16px;
      font-size: 16px;
      margin-right: 6px;
    }

    ::v-deep {

      .van-button__text {
        display: flex;
        align-items: center;
        color: #606266;
        font-size: 14px;
        font-weight: 500;
      }
    }
  }

  .delete-border {
    padding: 0;
    margin-bottom: 12px;
    border-bottom: 1px solid #dcdfe6;
  }

  .add-border {
    padding: 0;
    margin-bottom: 12px;
    border-bottom: 1px solid #dcdfe6;
  }

  .add-btn {
    margin: 12px 0 12px;
    display: block;
    height: 36px;
    width: 100%;
    background: #fff;
    color: var(--custom-color, #0096cc);
    border: none;

    .add-icon {
      display: inline-block;
      line-height: 16px;
      font-size: 16px;
      margin-right: 6px;
    }

    ::v-deep {

      .van-button__text {
        display: flex;
        align-items: center;
        color: var(--custom-color, #0096cc);
        font-size: 14px;
        font-weight: 500;
      }
    }
  }

  .delete-dialog {
    ::v-deep {
      .van-dialog__content {
        padding: 20px;
        text-align: center;
      }

      .van-dialog__confirm {
        .van-button__text {
          color: var(--custom-color, #0096cc);
        }
      }
    }
  }
}
</style>
